import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { CANONICAL_HOST } from '../utils/deepLinkConfig';

function InviteToCrewModal({ crew, onClose, currentUser, currentUserId, onSuccess }) {
  const [activeTab, setActiveTab] = useState('link'); // 'link' or 'followers'
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followers, setFollowers] = useState([]);
  const [selectedFollowers, setSelectedFollowers] = useState([]);
  const [sending, setSending] = useState(false);

  const effectiveUserId = currentUser?.id ?? currentUserId ?? null;

  useEffect(() => {
    if (!crew?.id) return;
    setLoading(true);
    generateInviteLink();
    fetchFollowers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crew?.id, effectiveUserId]);

  const generateInviteLink = async () => {
    try {
      if (crew.invite_token) {
        setInviteLink(`${CANONICAL_HOST}/c/${crew.invite_token}`);
        setLoading(false);
        return;
      }

      const token = (crypto?.randomUUID?.()
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
      ).replace(/-/g, '').slice(0, 16);

      const { error } = await supabase
        .from('crews')
        .update({ invite_token: token })
        .eq('id', crew.id);

      if (error) throw error;

      setInviteLink(`${CANONICAL_HOST}/c/${token}`);
      setLoading(false);
    } catch (error) {
      console.error('Error generating invite link:', error);
      setLoading(false);
    }
  };

  const fetchFollowers = async () => {
    try {
      if (!effectiveUserId) {
        setFollowers([]);
        return;
      }

      const { data: followData, error: followError } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', effectiveUserId);

      if (followError) throw followError;

      const followerIds = (followData || []).map(f => f.follower_id);
      if (followerIds.length === 0) {
        setFollowers([]);
        return;
      }

      const { data: memberData, error: memberError } = await supabase
        .from('crew_members')
        .select('user_id')
        .eq('crew_id', crew.id);

      if (memberError) throw memberError;

      const existingMemberIds = (memberData || []).map(m => m.user_id);
      const availableFollowerIds = followerIds.filter(id => !existingMemberIds.includes(id));

      if (availableFollowerIds.length === 0) {
        setFollowers([]);
        return;
      }

      // ✅ Your users table only has: id, last_streak_date, last_activity_at, timezone, fcm_token_updated_at
      // So only select id.
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .in('id', availableFollowerIds);

      if (userError) throw userError;

      setFollowers(userData || []);
    } catch (error) {
      console.error('Error fetching followers:', error);
      setFollowers([]);
    }
  };

  const incrementInvitesSent = async (count = 1) => {
    try {
      const { data: fresh, error: readErr } = await supabase
        .from('crews')
        .select('invites_sent')
        .eq('id', crew.id)
        .single();

      if (readErr) throw readErr;

      const next = (fresh?.invites_sent || 0) + count;

      const { error: writeErr } = await supabase
        .from('crews')
        .update({ invites_sent: next })
        .eq('id', crew.id);

      if (writeErr) throw writeErr;
    } catch (error) {
      console.error('Error updating invite count:', error);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      await incrementInvitesSent(1);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const shareLink = async () => {
    const shareData = {
      title: `Join ${crew.name} on GweffIT!`,
      text: `You've been invited to join the ${crew.name} crew on GweffIT.`,
      url: inviteLink
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        await incrementInvitesSent(1);
      } else {
        await copyLink();
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error sharing:', error);
    }
  };

  const toggleFollower = (followerId) => {
    setSelectedFollowers(prev =>
      prev.includes(followerId)
        ? prev.filter(id => id !== followerId)
        : [...prev, followerId]
    );
  };

  const inviteFollowers = async () => {
    if (selectedFollowers.length === 0 || !effectiveUserId) return;

    setSending(true);
    try {
      const invites = selectedFollowers.map(followerId => ({
        crew_id: crew.id,
        invited_user_id: followerId,
        invited_by_user_id: effectiveUserId,
        status: 'pending',
        created_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('crew_invites')
        .insert(invites);

      if (error) throw error;

      await incrementInvitesSent(selectedFollowers.length);

      if (onSuccess) onSuccess();

      alert(`Invited ${selectedFollowers.length} ${selectedFollowers.length === 1 ? 'person' : 'people'} to the crew!`);
      setSelectedFollowers([]);
      fetchFollowers();
    } catch (error) {
      console.error('Error inviting followers:', error);
      alert('Failed to send invites. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const shortId = (id) => {
    if (!id) return 'User';
    return `User ${id.slice(0, 6)}…${id.slice(-4)}`;
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Invite to {crew.name}</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(activeTab === 'link' ? styles.activeTab : {}) }}
            onClick={() => setActiveTab('link')}
          >
            🔗 Share Link
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'followers' ? styles.activeTab : {}) }}
            onClick={() => setActiveTab('followers')}
          >
            👥 Invite Followers
          </button>
        </div>

        {activeTab === 'link' && (
          <div style={styles.content}>
            {loading ? (
              <div style={styles.loading}>Generating invite link...</div>
            ) : (
              <>
                <p style={styles.description}>
                  Share this link with anyone you want to join <strong>{crew.name}</strong>
                </p>

                <div style={styles.linkBox}>
                  <input type="text" value={inviteLink} readOnly style={styles.linkInput} />
                  <button onClick={copyLink} style={styles.copyButton}>
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>

                <button onClick={shareLink} style={styles.shareButton}>
                  📤 Share Link
                </button>

                <div style={styles.stats}>
                  <span style={styles.statItem}>📊 {crew.invites_sent || 0} invites sent</span>
                  <span style={styles.statItem}>✅ {crew.invites_accepted || 0} joined</span>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'followers' && (
          <div style={styles.content}>
            {followers.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyTitle}>No available followers</p>
                <p style={styles.emptyText}>
                  Share the link above to invite anyone.
                </p>
              </div>
            ) : (
              <>
                <p style={styles.description}>
                  Select followers to invite to <strong>{crew.name}</strong>
                </p>

                <div style={styles.followerList}>
                  {followers.map(f => (
                    <div
                      key={f.id}
                      style={{
                        ...styles.followerItem,
                        ...(selectedFollowers.includes(f.id) ? styles.selectedFollower : {})
                      }}
                      onClick={() => toggleFollower(f.id)}
                    >
                      <div style={styles.followerAvatar}>
                        <span style={styles.avatarPlaceholder}>👤</span>
                      </div>
                      <span style={styles.followerName}>{shortId(f.id)}</span>
                      {selectedFollowers.includes(f.id) && <span style={styles.checkmark}>✓</span>}
                    </div>
                  ))}
                </div>

                <button
                  onClick={inviteFollowers}
                  disabled={selectedFollowers.length === 0 || sending}
                  style={{
                    ...styles.inviteButton,
                    ...(selectedFollowers.length === 0 ? styles.disabledButton : {})
                  }}
                >
                  {sending ? 'Sending...' : `Invite ${selectedFollowers.length || ''}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  header: {
    padding: '20px',
    borderBottom: '2px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: { margin: 0, fontSize: '24px', fontWeight: '900', color: '#7c3aed' },
  closeButton: {
    background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer',
    color: '#999', padding: '0', width: '32px', height: '32px',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  tabs: { display: 'flex', borderBottom: '2px solid #f0f0f0', backgroundColor: '#fafafa' },
  tab: {
    flex: 1, padding: '15px', border: 'none', background: 'none',
    fontSize: '16px', fontWeight: '600', cursor: 'pointer', color: '#666'
  },
  activeTab: { color: '#7c3aed', backgroundColor: 'white', borderBottom: '3px solid #7c3aed' },
  content: { padding: '20px', overflowY: 'auto', flex: 1 },
  loading: { textAlign: 'center', padding: '40px', color: '#999', fontSize: '16px' },
  description: { fontSize: '14px', color: '#666', marginBottom: '20px', lineHeight: '1.5' },
  linkBox: { display: 'flex', gap: '10px', marginBottom: '15px' },
  linkInput: {
    flex: 1, padding: '12px', border: '2px solid #a78bfa', borderRadius: '8px',
    fontSize: '14px', fontFamily: 'monospace', backgroundColor: '#f5f3ff', color: '#581c87'
  },
  copyButton: {
    padding: '12px 20px', backgroundColor: '#7c3aed', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', whiteSpace: 'nowrap'
  },
  shareButton: {
    width: '100%', padding: '15px', backgroundColor: '#ec4899', color: 'white',
    border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', marginBottom: '20px'
  },
  stats: {
    display: 'flex', gap: '15px', justifyContent: 'center',
    paddingTop: '15px', borderTop: '1px solid #f0f0f0'
  },
  statItem: { fontSize: '14px', color: '#666', fontWeight: '600' },
  emptyState: { textAlign: 'center', padding: '40px 20px' },
  emptyTitle: { fontSize: '18px', fontWeight: '700', color: '#333', marginBottom: '10px' },
  emptyText: { fontSize: '14px', color: '#666', lineHeight: '1.5' },
  followerList: {
    display: 'flex', flexDirection: 'column', gap: '8px',
    marginBottom: '20px', maxHeight: '300px', overflowY: 'auto'
  },
  followerItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px', borderRadius: '8px', border: '2px solid #e0e0e0',
    cursor: 'pointer'
  },
  selectedFollower: { borderColor: '#7c3aed', backgroundColor: '#f5f3ff' },
  followerAvatar: {
    width: '40px', height: '40px', borderRadius: '50%',
    backgroundColor: '#e0e0e0', display: 'flex',
    alignItems: 'center', justifyContent: 'center'
  },
  avatarPlaceholder: { fontSize: '18px', fontWeight: '700', color: '#666' },
  followerName: { flex: 1, fontSize: '15px', fontWeight: '600', color: '#333' },
  checkmark: { fontSize: '20px', color: '#7c3aed', fontWeight: '900' },
  inviteButton: {
    width: '100%', padding: '15px', backgroundColor: '#7c3aed', color: 'white',
    border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700',
    cursor: 'pointer'
  },
  disabledButton: { backgroundColor: '#ccc', cursor: 'not-allowed' }
};

export default InviteToCrewModal;
